import { FC, MutableRefObject, useRef } from 'react';
import { v4 } from 'uuid';
import { beautifyThePrice, isArray, isNotEmpty } from '@lhb/func';
import { useMethods } from '@lhb/hook';
import { SimilarStore, SimilarStoreModule, SmartDecisionValue } from './types';
import styles from './index.module.less';
import V2Table from '@/common/components/Data/V2Table';
import ShopDetailDrawer from '@/views/expandstore/pages/shopDetail/Drawer';

interface SimilarStoresProps {
  similarStoreModule?: SimilarStoreModule;
  shared?: boolean;
}

type SimilarStoreRow = SimilarStore & {
  id: string;
  isAverage?: boolean;
};

/**
 * @description 相似门店数据
 */
const SimilarStores: FC<SimilarStoresProps> = ({ similarStoreModule, shared }) => {
  const storeDetailRef: MutableRefObject<any> = useRef(null);

  const methods = useMethods({
    formatNumber(value: SmartDecisionValue) {
      if (!isNotEmpty(value)) return '-';
      return beautifyThePrice(value as any, ',', 'auto');
    },
    formatPercent(value: SmartDecisionValue) {
      if (!isNotEmpty(value)) return '-';
      return `${value}%`;
    },
    formatMoney(value: SmartDecisionValue) {
      if (!isNotEmpty(value)) return '-';
      return `¥${methods.formatNumber(value)}`;
    },
    getRows(): SimilarStoreRow[] {
      const stores = similarStoreModule?.stores && isArray(similarStoreModule.stores) ? similarStoreModule.stores : [];
      const storeRows = stores.map((store) => ({
        ...store,
        id: v4(),
      }));
      if (!similarStoreModule?.average) return storeRows;
      return [
        ...storeRows,
        {
          ...similarStoreModule.average,
          storeName: similarStoreModule.average.storeName || '相似店均值',
          id: v4(),
          isAverage: true,
        },
      ];
    },
    renderStoreName(value: string, record: SimilarStoreRow) {
      if (record.isAverage) {
        return <span className={styles.averageText}>{value || '-'}</span>;
      }
      return (
        <span
          className={shared ? '' : styles.linkCell}
          onClick={methods.openStoreDetail.bind(null, record.storeId)}
        >
          {value || '-'}
        </span>
      );
    },
    openStoreDetail(storeId?: number) {
      // 分享
      if (shared) return;
      if (!isNotEmpty(storeId)) return;
      storeDetailRef.current?.init?.(storeId);
    },
    reloadStore() {},
    renderFlow(value: SmartDecisionValue) {
      return methods.formatNumber(value);
    },
    renderEnterRate(value: SmartDecisionValue) {
      return (
        <span className='font-weight-500 ff-medium c-ff8'>
          {methods.formatPercent(value)}
        </span>
      );
    },
    renderConversionRate(value: SmartDecisionValue) {
      return methods.formatPercent(value);
    },
    renderUnitPrice(value: SmartDecisionValue) {
      return methods.formatMoney(value);
    },
    renderDailySales(value: SmartDecisionValue) {
      return methods.formatMoney(value);
    },
    getColumns() {
      return [
        {
          key: 'storeName',
          title: '门店名称',
          width: 160,
          render: methods.renderStoreName,
        },
        {
          key: 'passbyFlow',
          title: '过店客流',
          width: 160,
          render: methods.renderFlow,
        },
        {
          key: 'enterRate',
          title: '进店率',
          width: 160,
          render: methods.renderEnterRate,
        },
        {
          key: 'conversionRate',
          title: '转化率',
          width: 160,
          render: methods.renderConversionRate,
        },
        {
          key: 'unitPrice',
          title: '客单价',
          width: 160,
          render: methods.renderUnitPrice,
        },
        {
          key: 'dailySales',
          title: '日均销售额',
          width: 'auto',
          render: methods.renderDailySales,
        },
      ];
    },
    loadTable() {
      const dataSource = methods.getRows();
      return Promise.resolve({
        dataSource,
        count: dataSource.length,
      });
    },
    getRowClassName(record: SimilarStoreRow) {
      return record.isAverage ? styles.averageRow : '';
    },
  });

  // 依赖相似门店数据变化，确保数据到达后触发 V2Table 重新请求
  // const tableFilters = useMemo(() => ({ similarStoreModule }), [similarStoreModule]);

  return (
    <div className={styles.detailCard}>
      <div className={styles.chartTitle}>相似门店</div>
      <V2Table
        rowKey='id'
        defaultColumns={methods.getColumns()}
        onFetch={methods.loadTable}
        // 数据异步到达后通过变更 filters 触发 V2Table 重新请求
        filters={{}}
        pagination={false}
        emptyRender
        bordered={false}
        hideColumnPlaceholder
        className={styles.similarTable}
        rowClassName={methods.getRowClassName}
      />
      <ShopDetailDrawer
        ref={storeDetailRef}
        reload={methods.reloadStore}
        readonly
      />
    </div>
  );
};

export default SimilarStores;
